import { useEffect, useState, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Waves, X, Loader2, Download, RefreshCw, Map as MapIcon, Crosshair, FileDown,
  MapPin, Upload, Trash2, FileText, Route, Layers, ShieldCheck, Plus, Table, ClipboardList, AlertTriangle, AlertOctagon } from "lucide-react";
import BoreholeOverlay, { THM_RAMP, thmColor, VALIDATION_C, VALIDATION_LABEL } from "./BoreholeOverlay";

const TabBtn = ({ active, onClick, testid, icon: Icon, label }) => (
  <button data-testid={testid} onClick={onClick}
    className={`h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors ${active ? "bg-[#C20511] text-white" : "bg-white/10 text-slate-200 hover:bg-white/20"}`}>
    <Icon size={13} /> {label}
  </button>
);

const TEMPLATE_CSV = "hole_id,lon,lat,from_m,to_m,thm_pct,ilmenite_pct,rutile_pct,zircon_pct\n"
  + "DH001,39.2401,-6.8123,0,4,3.1,2.0,0.3,0.2\n"
  + "DH001,39.2401,-6.8123,4,9,7.8,5.1,0.7,0.5\n"
  + "DH001,39.2401,-6.8123,9,14.5,5.4,3.4,0.5,0.4\n"
  + "DH002,39.2455,-6.8090,0,5,4.1,2.6,0.4,0.3\n"
  + "DH002,39.2455,-6.8090,5,11,2.2,1.3,0.2,0.2\n";

// HMS Geomorphological Analysis viewer: Geomorphology / Field Plan / Boreholes tabs. The Boreholes
// tab plots operator drillhole data (THM grade heatmap), a corridor-snap report and a Section-from-
// holes tool - all fed into the Master PDF.
export default function GeomorphologyDialog({ open, onOpenChange, projectId, areaId, areaName }) {
  const [tab, setTab] = useState("geo");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);
  const [fp, setFp] = useState(null);
  const [fpLoading, setFpLoading] = useState(false);
  const fpStarted = useRef(false);
  const [bh, setBh] = useState(null);
  const [bhLoading, setBhLoading] = useState(false);
  const bhStarted = useRef(false);
  const [selectedHole, setSelectedHole] = useState(null);
  const fileRef = useRef(null);
  const [sectionMode, setSectionMode] = useState(false);
  const [sectionIds, setSectionIds] = useState([]);
  const [sectionUri, setSectionUri] = useState(null);
  const [sectionBusy, setSectionBusy] = useState(false);
  const [showSurface, setShowSurface] = useState(true);
  const [validationMode, setValidationMode] = useState(false);
  const [editIntervals, setEditIntervals] = useState(false);
  const [draftIntervals, setDraftIntervals] = useState([]);
  const [intervalBusy, setIntervalBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkFileRef = useRef(null);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError(false);
    setData(null);
    try {
      const { data: d } = await api.get(`/projects/${projectId}/areas/${areaId}/geomorphology`);
      if (!signal?.cancelled) setData(d);
    } catch (e) {
      if (!signal?.cancelled) { setError(true); toast.error(apiError(e)); }
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, [projectId, areaId]);

  useEffect(() => {
    if (!open) return undefined;
    setTab("geo");
    setFp(null); fpStarted.current = false;
    setBh(null); bhStarted.current = false;
    setSelectedHole(null);
    setSectionMode(false); setSectionIds([]); setSectionUri(null);
    setValidationMode(false); setShowSurface(true); setEditIntervals(false); setDraftIntervals([]);
    const signal = { cancelled: false };
    load(signal);
    return () => { signal.cancelled = true; };
  }, [open, load]);

  useEffect(() => {
    if (!open || tab !== "field" || fp || fpStarted.current) return undefined;
    fpStarted.current = true;
    let cancelled = false;
    (async () => {
      setFpLoading(true);
      try {
        const { data: d } = await api.get(`/projects/${projectId}/areas/${areaId}/field-plan`);
        if (!cancelled) setFp(d);
      } catch (e) {
        if (!cancelled) toast.error(apiError(e));
      } finally {
        if (!cancelled) setFpLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, tab, fp, projectId, areaId]);

  const loadBoreholes = useCallback(async () => {
    setBhLoading(true);
    try {
      const { data: d } = await api.get(`/projects/${projectId}/areas/${areaId}/boreholes`);
      setBh(d);
      const _ids = new Set((d.boreholes || []).map((h) => h.hole_id));
      setSectionIds((d.section_hole_ids || []).filter((id) => _ids.has(id)));
      setSectionUri(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBhLoading(false);
    }
  }, [projectId, areaId]);

  useEffect(() => {
    if (!open || tab !== "boreholes" || bh || bhStarted.current) return undefined;
    bhStarted.current = true;
    loadBoreholes();
    return undefined;
  }, [open, tab, bh, loadBoreholes]);

  const corridors = data?.corridors || [];
  const holes = bh?.boreholes || [];
  const bbox = bh?.bbox;
  const outCount = bbox ? holes.filter((h) => h.lon < bbox[0] || h.lon > bbox[2] || h.lat < bbox[1] || h.lat > bbox[3]).length : 0;
  const gradeFields = bh?.grade_fields || [];
  const thmField = bh?.thm_field;
  const selected = holes.find((h) => h.hole_id === selectedHole) || null;

  const resetHoleDerived = () => { setSelectedHole(null); setSectionIds([]); setSectionUri(null); setSectionMode(false); };

  const downloadMapImg = () => {
    if (!data?.uri) return;
    const a = document.createElement("a");
    a.href = data.uri; a.download = `SYMIN_${areaId}_Geomorphology.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const downloadFieldPlan = async (fmt) => {
    const tid = toast.loading(`Preparing ${fmt.toUpperCase()} export…`);
    try {
      const r = await api.get(`/projects/${projectId}/areas/${areaId}/field-plan/export?fmt=${fmt}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = `SYMIN_${areaId}_FieldPlan.${fmt === "geojson" ? "geojson" : "csv"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
      toast.success("Field plan exported", { id: tid });
    } catch (e) {
      toast.error(apiError(e), { id: tid });
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    const tid = toast.loading("Uploading drillhole data…");
    try {
      const { data: r } = await api.post(`/projects/${projectId}/areas/${areaId}/boreholes/upload`, form);
      setBh((prev) => ({ ...(prev || {}), boreholes: r.boreholes, grade_fields: r.grade_fields, thm_field: r.thm_field }));
      resetHoleDerived();
      // re-fetch to pick up recomputed snap + thm_field for the fresh holes
      loadBoreholes();
      toast.success(`${r.count} drillhole(s) loaded${r.interval_holes ? ` · ${r.interval_holes} with down-hole intervals` : ""}`, { id: tid });
      const warns = (r.issues || []).filter((it) => it.level !== "error");
      if (warns.length) toast.warning(warns.length > 1 ? `${warns.length} data warnings - e.g. ${warns[0].message}` : warns[0].message);
    } catch (err) {
      toast.error(apiError(err), { id: tid });
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearHoles = async () => {
    try {
      await api.delete(`/projects/${projectId}/areas/${areaId}/boreholes`);
      setBh((prev) => ({ ...(prev || {}), boreholes: [], grade_fields: [] }));
      resetHoleDerived();
      toast.success("Drillholes cleared");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = "SYMIN_borehole_template.csv";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
  };

  const openBulk = () => { setBulkText(""); setBulkPreview(null); setBulkOpen(true); };
  const onBulkFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setBulkText(text); setBulkPreview(null);
    if (bulkFileRef.current) bulkFileRef.current.value = "";
  };
  const runBulkPreview = async () => {
    if (!bulkText.trim()) { toast.error("Paste CSV text or choose a file first"); return; }
    setBulkBusy(true);
    try {
      const { data: r } = await api.post(`/projects/${projectId}/areas/${areaId}/boreholes/import`, { text: bulkText, commit: false });
      setBulkPreview(r);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBulkBusy(false); }
  };
  const commitBulk = async () => {
    if (!bulkPreview?.count) return;
    setBulkBusy(true);
    const tid = toast.loading(`Importing ${bulkPreview.count} drillhole(s)…`);
    try {
      const { data: r } = await api.post(`/projects/${projectId}/areas/${areaId}/boreholes/import`, { text: bulkText, commit: true });
      setBh((prev) => ({ ...(prev || {}), boreholes: r.boreholes, grade_fields: r.grade_fields, thm_field: r.thm_field }));
      resetHoleDerived();
      loadBoreholes();
      setBulkOpen(false);
      toast.success(`${r.count} drillhole(s) imported${r.interval_holes ? ` · ${r.interval_holes} with intervals` : ""}`, { id: tid });
    } catch (e) {
      toast.error(apiError(e), { id: tid });
    } finally { setBulkBusy(false); }
  };
  const buildSampleCsv = () => {
    const b = bh?.bbox;
    if (!b) return TEMPLATE_CSV;
    const [minx, miny, maxx, maxy] = b;
    const cx = (minx + maxx) / 2, cy = (miny + maxy) / 2;
    const ox = (maxx - minx) * 0.12, oy = (maxy - miny) * 0.12;
    const f = (n) => Number(n).toFixed(6);
    const p1 = `${f(cx - ox)},${f(cy + oy)}`, p2 = `${f(cx + ox)},${f(cy - oy)}`;
    return "hole_id,lon,lat,from_m,to_m,thm_pct,ilmenite_pct,rutile_pct,zircon_pct\n"
      + `DH001,${p1},0,4,3.1,2.0,0.3,0.2\n`
      + `DH001,${p1},4,9,7.8,5.1,0.7,0.5\n`
      + `DH001,${p1},9,14.5,5.4,3.4,0.5,0.4\n`
      + `DH002,${p2},0,5,4.1,2.6,0.4,0.3\n`
      + `DH002,${p2},5,11,2.2,1.3,0.2,0.2\n`;
  };
  const bulkOut = bh?.bbox && bulkPreview?.boreholes
    ? bulkPreview.boreholes.filter((h) => h.lon < bh.bbox[0] || h.lon > bh.bbox[2] || h.lat < bh.bbox[1] || h.lat > bh.bbox[3]).length
    : 0;

  const downloadPlate = async () => {
    const tid = toast.loading("Rendering high-res plate…");
    try {
      const r = await api.get(`/projects/${projectId}/areas/${areaId}/borehole-overlay?download=1${validationMode ? "&validate=1" : ""}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = `SYMIN_${areaId}_Borehole_Overlay.png`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
      toast.success("Plate downloaded", { id: tid });
    } catch (e) {
      toast.error(apiError(e), { id: tid });
    }
  };

  const toggleSectionHole = (id) => {
    setSectionUri(null);
    setSectionIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const generateSection = async () => {
    if (sectionIds.length < 2) { toast.error("Select at least 2 holes for the section"); return; }
    setSectionBusy(true);
    const tid = toast.loading("Building down-dip section…");
    try {
      const { data: d } = await api.post(`/projects/${projectId}/areas/${areaId}/section`, { hole_ids: sectionIds });
      setSectionUri(d.uri);
      setTimeout(() => document.querySelector('[data-testid="section-preview"]')?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 120);
      toast.success(`Section A–A′ generated (${d.section?.total_length_m ?? 0} m)`, { id: tid });
    } catch (e) {
      toast.error(apiError(e), { id: tid });
    } finally {
      setSectionBusy(false);
    }
  };

  const clearSection = async () => {
    try {
      await api.delete(`/projects/${projectId}/areas/${areaId}/section`);
    } catch (e) { /* ignore */ }
    setSectionIds([]); setSectionUri(null);
    toast.success("Section cleared");
  };

  const downloadSection = async () => {
    const tid = toast.loading("Rendering section plate…");
    try {
      const r = await api.get(`/projects/${projectId}/areas/${areaId}/section-plate?download=1`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = `SYMIN_${areaId}_Drillhole_Section.png`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
      toast.success("Section downloaded", { id: tid });
    } catch (e) {
      toast.error(apiError(e), { id: tid });
    }
  };

  const startEditIntervals = () => {
    setDraftIntervals((selected?.intervals || []).map((iv) => ({ from_m: iv.from_m, to_m: iv.to_m, grades: { ...(iv.grades || {}) } })));
    setEditIntervals(true);
  };
  const addDraftInterval = () => {
    setDraftIntervals((p) => {
      const last = p[p.length - 1];
      const from = last ? Number(last.to_m) || 0 : 0;
      return [...p, { from_m: from, to_m: from + 5, grades: {} }];
    });
  };
  const updateDraft = (i, key, val) => setDraftIntervals((p) => p.map((iv, j) => (j === i ? { ...iv, [key]: val } : iv)));
  const updateDraftGrade = (i, field, val) =>
    setDraftIntervals((p) => p.map((iv, j) => (j === i ? { ...iv, grades: { ...iv.grades, [field]: val } } : iv)));
  const removeDraft = (i) => setDraftIntervals((p) => p.filter((_, j) => j !== i));
  const saveIntervals = async () => {
    if (!selected) return;
    setIntervalBusy(true);
    const tid = toast.loading("Compositing down-hole intervals…");
    try {
      const clean = draftIntervals
        .map((iv) => ({
          from_m: Number(iv.from_m), to_m: Number(iv.to_m),
          grades: Object.fromEntries(Object.entries(iv.grades || {})
            .filter(([, v]) => v !== "" && v != null && !isNaN(Number(v)))
            .map(([k, v]) => [k, Number(v)])),
        }))
        .filter((iv) => !isNaN(iv.from_m) && !isNaN(iv.to_m) && iv.to_m > iv.from_m);
      await api.post(`/projects/${projectId}/areas/${areaId}/boreholes/${selected.hole_id}/intervals`, { intervals: clean });
      toast.success("Down-hole intervals saved & composited", { id: tid });
      setEditIntervals(false);
      await loadBoreholes();
    } catch (e) {
      toast.error(apiError(e), { id: tid });
    } finally {
      setIntervalBusy(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[94vw] w-[94vw] h-[92vh] p-0 gap-0 flex flex-col overflow-hidden [&>button]:hidden"
        data-testid="geomorphology-dialog"
      >
        <div className="flex items-center justify-between px-5 h-14 border-b border-black/20 shrink-0" style={{ background: "#0B1420" }}>
          <DialogTitle className="text-sm font-bold flex items-center gap-2" style={{ color: "#FFFFFF", fontFamily: "Manrope, sans-serif" }}>
            <Waves size={16} /> HMS Geomorphological Analysis - {areaName || areaId}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Multi-sensor fusion interpreting palaeo-strandlines, depositional ridges and HMS corridors, plus an interactive
            overlay of the operator's drillhole data (THM grade heatmap), a corridor-snap report and a down-dip grade section tool.
          </DialogDescription>
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5 mr-1">
              <TabBtn testid="geomorph-tab-geo" active={tab === "geo"} onClick={() => setTab("geo")} icon={MapIcon} label="Geomorphology" />
              <TabBtn testid="geomorph-tab-field" active={tab === "field"} onClick={() => setTab("field")} icon={Crosshair} label="Field Plan" />
              <TabBtn testid="geomorph-tab-boreholes" active={tab === "boreholes"} onClick={() => setTab("boreholes")} icon={MapPin} label="Boreholes" />
            </div>
            {tab === "geo" && (
              <button data-testid="geomorph-download" onClick={downloadMapImg} disabled={!data?.uri}
                className="h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 bg-white/10 text-white hover:bg-white/20 disabled:opacity-40">
                <Download size={13} /> Map
              </button>
            )}
            {tab === "field" && (
              <>
                <button data-testid="fieldplan-download-csv" onClick={() => downloadFieldPlan("csv")} disabled={!fp}
                  className="h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 bg-white/10 text-white hover:bg-white/20 disabled:opacity-40">
                  <FileDown size={13} /> CSV
                </button>
                <button data-testid="fieldplan-download-geojson" onClick={() => downloadFieldPlan("geojson")} disabled={!fp}
                  className="h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40" style={{ background: "#C20511", color: "#FFFFFF" }}>
                  <Download size={13} /> GeoJSON
                </button>
              </>
            )}
            {tab === "boreholes" && (
              <>
                <button data-testid="borehole-surface-toggle" onClick={() => setShowSurface((v) => !v)} disabled={!holes.length}
                  className="h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40 transition-colors"
                  style={showSurface ? { background: "#C20511", color: "#FFFFFF" } : { background: "rgba(255,255,255,0.1)", color: "#E2E8F0" }}>
                  <Layers size={13} /> Surface
                </button>
                <button data-testid="borehole-validation-toggle" onClick={() => setValidationMode((v) => !v)} disabled={!holes.length}
                  className="h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40 transition-colors"
                  style={validationMode ? { background: "#C20511", color: "#FFFFFF" } : { background: "rgba(255,255,255,0.1)", color: "#E2E8F0" }}>
                  <ShieldCheck size={13} /> Validate
                </button>
                <button data-testid="borehole-plate-btn" onClick={downloadPlate} disabled={!holes.length}
                  className="h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40" style={{ background: "#C20511", color: "#FFFFFF" }}>
                  <Download size={13} /> Plate
                </button>
              </>
            )}
            <button data-testid="geomorph-close" onClick={() => onOpenChange(false)} className="h-8 w-8 grid place-items-center rounded-md text-slate-300 hover:bg-white/10">
              <X size={16} />
            </button>
          </div>
        </div>

        {tab === "geo" && (
          <div className="flex-1 overflow-auto bg-slate-100 p-4 grid lg:grid-cols-[1fr_360px] gap-4" data-testid="geomorph-geo-view">
            <div className="space-y-4 min-w-0">
              <div className="bg-white rounded-xl border border-slate-200 flex items-start justify-center overflow-hidden min-h-[300px] p-2" data-testid="geomorph-map-wrap">
                {loading ? (
                  <div className="flex flex-col items-center gap-2 text-slate-500 py-20 m-auto">
                    <Loader2 className="animate-spin" /><span className="text-sm">Fusing DEM + SAR relief + Sentinel-2…</span>
                  </div>
                ) : error ? (
                  <div className="flex flex-col items-center gap-3 text-slate-500 py-20 m-auto" data-testid="geomorph-error">
                    <span className="text-sm">Could not load the geomorphology analysis.</span>
                    <button onClick={() => load()} data-testid="geomorph-retry"
                      className="h-9 px-4 rounded-md text-xs font-semibold flex items-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800">
                      <RefreshCw size={13} /> Retry
                    </button>
                  </div>
                ) : data?.uri ? (
                  <img src={data.uri} alt="HMS geomorphology map" className="w-full h-auto object-contain" data-testid="geomorph-map-img" />
                ) : (
                  <div className="text-sm text-slate-500 py-20 m-auto" data-testid="geomorph-empty">No geomorphology map available - generate this area first.</div>
                )}
              </div>
              {data?.sections_uri && (
                <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="geomorph-sections">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Corridor Cross-Sections (Ridge &amp; Swale)</h3>
                  <img src={data.sections_uri} alt="HMS corridor cross-sections" className="w-full object-contain" data-testid="geomorph-sections-img" />
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="geomorph-stats">
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Corridor Metrics</h3>
                {corridors.length === 0 ? (
                  <p className="text-sm text-slate-500" data-testid="geomorph-no-corridors">{loading ? "Analysing…" : "No coherent HMS corridor resolved above threshold."}</p>
                ) : (
                  <div className="space-y-3">
                    {corridors.map((c) => (
                      <div key={c.id} className="border border-slate-200 rounded-lg p-3" data-testid={`geomorph-corridor-${c.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-slate-900">{c.id}</span>
                          <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: "#FDECEC", color: "#92400E" }}>{c.orientation}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center text-xs">
                          <div><div className="font-bold text-slate-900 tabular-nums">{c.length_km}</div><div className="text-slate-500">Length km</div></div>
                          <div><div className="font-bold text-slate-900 tabular-nums">{c.width_km}</div><div className="text-slate-500">Width km</div></div>
                          <div><div className="font-bold text-slate-900 tabular-nums">{c.mean_score}</div><div className="text-slate-500">HMS score</div></div>
                        </div>
                        {c.tonnage && (
                          <div className="mt-2 text-[11px] text-slate-600 border-t border-slate-100 pt-2">
                            Indic. HM: <b className="text-slate-900">{c.tonnage.hm_mt_low}–{c.tonnage.hm_mt_high} Mt</b> · grade {c.tonnage.thm_grade_low}–{c.tonnage.thm_grade_high}%
                          </div>
                        )}
                        <p className="text-[11px] text-slate-600 mt-2 leading-snug">{c.extension?.note}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-around text-center" data-testid="geomorph-landform-counts">
                <div><div className="text-2xl font-bold" style={{ color: "#2A6F97" }}>{data?.strandline_count ?? 0}</div><div className="text-xs text-slate-500 mt-0.5">Palaeo-strandlines</div></div>
                <div><div className="text-2xl font-bold" style={{ color: "#8A5A2B" }}>{data?.ridge_count ?? 0}</div><div className="text-xs text-slate-500 mt-0.5">Depositional ridges</div></div>
              </div>
              <p className="text-[11px] text-slate-400 leading-snug px-1">
                Interpreted from multi-sensor fusion of the Copernicus DEM GLO-30, Sentinel-1 radar relief and the Sentinel-2 heavy-mineral-sands prospectivity model. Tonnages are conceptual (not a Mineral Resource).
              </p>
            </div>
          </div>
        )}

        {tab === "field" && (
          <div className="flex-1 overflow-auto bg-slate-100 p-4 grid lg:grid-cols-[1fr_340px] gap-4" data-testid="geomorph-field-view">
            <div className="bg-white rounded-xl border border-slate-200 flex items-start justify-center overflow-hidden min-h-[300px] p-2" data-testid="fieldplan-map-wrap">
              {fpLoading ? (
                <div className="flex flex-col items-center gap-2 text-slate-500 py-20 m-auto">
                  <Loader2 className="animate-spin" /><span className="text-sm">Generating traverse lines & sample stations…</span>
                </div>
              ) : fp?.uri ? (
                <img src={fp.uri} alt="HMS field sampling plan" className="w-full h-auto object-contain" data-testid="fieldplan-map-img" />
              ) : (
                <div className="text-sm text-slate-500 py-20 m-auto" data-testid="fieldplan-empty">No field plan available.</div>
              )}
            </div>
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="fieldplan-summary">
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Sampling Plan</h3>
                {fp?.plan ? (
                  <div className="grid grid-cols-2 gap-2 text-center text-sm">
                    <div className="border border-slate-200 rounded-lg p-2"><div className="font-bold text-slate-900 tabular-nums">{fp.plan.total_traverses}</div><div className="text-[11px] text-slate-500">Traverse lines</div></div>
                    <div className="border border-slate-200 rounded-lg p-2"><div className="font-bold text-slate-900 tabular-nums">{fp.plan.total_samples}</div><div className="text-[11px] text-slate-500">Sample stations</div></div>
                    <div className="border border-slate-200 rounded-lg p-2"><div className="font-bold text-slate-900 tabular-nums">{fp.plan.line_spacing_m}m</div><div className="text-[11px] text-slate-500">Line spacing</div></div>
                    <div className="border border-slate-200 rounded-lg p-2"><div className="font-bold text-slate-900 tabular-nums">{fp.plan.sample_spacing_m}m</div><div className="text-[11px] text-slate-500">Sample spacing</div></div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">{fpLoading ? "Generating…" : "No corridors to sample."}</p>
                )}
              </div>
              {(fp?.corridors || []).length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2" data-testid="fieldplan-corridors">
                  {fp.corridors.map((c) => (
                    <div key={c.id} className="flex items-center justify-between text-xs border-b border-slate-100 pb-2 last:border-0 last:pb-0" data-testid={`fieldplan-corridor-${c.id}`}>
                      <span className="font-bold text-slate-900">{c.id}</span>
                      <span className="text-slate-600">{c.n_traverses} lines · {c.n_samples} pts · az {c.traverse_azimuth_deg}°</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-slate-400 leading-snug px-1">
                Suggested drill/auger traverses run perpendicular to each corridor's strike. Export WGS84 sample coordinates as CSV or GeoJSON for the field team.
              </p>
            </div>
          </div>
        )}

        {tab === "boreholes" && (
          <div className="flex-1 overflow-auto bg-slate-100 p-4 grid lg:grid-cols-[1fr_340px] gap-4 items-start" data-testid="geomorph-borehole-view">
            <div className="space-y-4 min-w-0">
              <div className="bg-white rounded-xl border border-slate-200 flex items-start justify-center overflow-hidden min-h-[280px] p-3"
                data-testid="borehole-map-wrap" onClick={() => !sectionMode && setSelectedHole(null)}>
                {bhLoading ? (
                  <div className="flex flex-col items-center gap-2 text-slate-500 py-20 m-auto">
                    <Loader2 className="animate-spin" /><span className="text-sm">Loading interpreted landforms…</span>
                  </div>
                ) : bh ? (
                  <div className="w-full" onClick={(e) => e.stopPropagation()}>
                    <BoreholeOverlay bbox={bh.bbox} boundary={bh.boundary} geo={bh.geo}
                      boreholes={holes} gradeFields={gradeFields} thmField={thmField}
                      selectedId={selectedHole} onSelect={setSelectedHole}
                      showSurface={showSurface} validationMode={validationMode} contours={bh.thm_contours || []}
                      sectionMode={sectionMode} sectionIds={sectionIds} onToggleHole={toggleSectionHole} />
                    {holes.length === 0 && (
                      <p className="text-center text-xs text-slate-400 mt-3" data-testid="borehole-empty-hint">
                        Upload or bulk-import your drillhole CSV to plot them, coloured by THM grade, on the interpreted landforms.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-slate-500 py-20 m-auto">No data available.</div>
                )}
              </div>
              {sectionUri && (
                <div className="bg-white rounded-xl border border-slate-200 p-2" data-testid="section-preview">
                  <div className="flex items-center justify-between px-1 mb-1">
                    <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Drillhole Grade Section (A–A′)</span>
                    <button data-testid="section-download-btn" onClick={downloadSection}
                      className="h-7 px-2.5 rounded-md text-[11px] font-semibold flex items-center gap-1 bg-slate-900 text-white hover:bg-slate-800">
                      <Download size={12} /> PNG
                    </button>
                  </div>
                  <img src={sectionUri} alt="Drillhole grade section" className="w-full object-contain rounded" data-testid="section-img" />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="borehole-data-card">
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Drillhole Data</h3>
                <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onUpload} className="hidden" data-testid="borehole-file-input" />
                <button data-testid="borehole-upload-btn" onClick={() => fileRef.current?.click()}
                  className="w-full h-9 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5" style={{ background: "#0B1420", color: "#FFFFFF" }}>
                  <Upload size={14} /> Upload drillhole CSV
                </button>
                <button data-testid="borehole-bulk-btn" onClick={openBulk}
                  className="w-full h-9 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 border border-[#C20511]" style={{ background: "#FDECEC", color: "#92400E" }}>
                  <ClipboardList size={14} /> Guided bulk import
                </button>
                <button data-testid="borehole-clear-btn" onClick={clearHoles} disabled={!holes.length}
                  className="w-full h-9 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
                  <Trash2 size={14} /> Clear drillholes
                </button>
                <button data-testid="borehole-template-btn" onClick={downloadTemplate}
                  className="w-full h-8 rounded-md text-[11px] font-semibold flex items-center justify-center gap-1.5 text-slate-500 hover:text-slate-800">
                  <FileText size={13} /> Download CSV template
                </button>
                <div className="text-[10px] text-slate-500 leading-snug bg-slate-50 rounded-md px-2.5 py-2 border border-slate-100" data-testid="interval-import-help">
                  <span className="font-bold text-slate-600">Bulk down-hole assays:</span> add <b>from_m</b> &amp; <b>to_m</b> columns and one row per interval per hole (many holes at once) - SYMIN composites each hole automatically and the section reads true grade-by-depth. The template above is pre-filled with the interval format.
                </div>
                <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-100">
                  <span className="text-slate-500">Holes plotted</span>
                  <span className="font-bold text-slate-900 tabular-nums" data-testid="borehole-count">{holes.length}</span>
                </div>
                {outCount > 0 && (
                  <div className="text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 leading-snug" data-testid="borehole-outside-note">
                    {outCount} hole{outCount > 1 ? "s" : ""} fall{outCount > 1 ? "" : "s"} outside this licence AOI (clamped to the map edge). Check the coordinate system.
                  </div>
                )}
                {gradeFields.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1" data-testid="borehole-grade-fields">
                    {gradeFields.map((g) => (
                      <span key={g} className="text-[10px] font-semibold px-2 py-0.5 rounded" style={{ background: "#FDECEC", color: "#92400E" }}>{g}</span>
                    ))}
                  </div>
                )}
              </div>

              {thmField && (
                <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="borehole-heatmap-legend">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">THM Grade Heatmap · {thmField}</h3>
                  <div className="flex items-center gap-1">
                    {THM_RAMP.map((s, i) => (<div key={i} className="h-3 flex-1 rounded-sm" style={{ background: s.color }} />))}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                    {THM_RAMP.map((s, i) => (
                      <span key={i} className="flex items-center gap-1 text-[10px] text-slate-600">
                        <i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: s.color }} /> {s.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {validationMode && bh?.validation && (
                <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2" data-testid="borehole-validation-card">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><ShieldCheck size={13} /> Model vs Ground-Truth</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {[["confirm", "Confirms", "#15803D"], ["upside", "Upside", "#D97706"], ["downside", "Downside", "#DC2626"], ["neutral", "No grade", "#94A3B8"]].map(([k, label, c]) => (
                      <div key={k} className="flex items-center justify-between rounded-md px-2.5 py-1.5" style={{ background: `${c}14` }} data-testid={`validation-count-${k}`}>
                        <span className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: c }}>
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} /> {label}
                        </span>
                        <span className="text-sm font-bold" style={{ color: c }}>{bh.validation.summary?.[k] ?? 0}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 leading-snug">
                    Each hole's logged {thmField || "THM"} grade vs its nearest corridor's HMS score (grade ≥ {bh.validation.grade_hi}% · model ≥ {Math.round(bh.validation.score_hi)}/100). Markers are recoloured by verdict.
                  </p>
                </div>
              )}

              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="borehole-section-card">
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><Route size={13} /> Grade Section (A–A′)</h3>
                <button data-testid="section-mode-toggle" onClick={() => setSectionMode((v) => !v)} disabled={holes.length < 2}
                  className="w-full h-9 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40 transition-colors"
                  style={sectionMode ? { background: "#0B1420", color: "#FFFFFF" } : { background: "#F1F5F9", color: "#0F172A" }}>
                  {sectionMode ? "Selecting - click holes in order" : "Select holes for section"}
                </button>
                {sectionIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="section-selected">
                    {sectionIds.map((id, i) => (
                      <button key={id} data-testid={`section-chip-${id}`} onClick={() => toggleSectionHole(id)}
                        className="flex items-center gap-1 text-[11px] font-semibold pl-1 pr-2 py-0.5 rounded-full border border-slate-300 hover:border-red-300 hover:bg-red-50">
                        <span className="grid place-items-center rounded-full text-[9px]" style={{ width: 15, height: 15, background: "#0B1420", color: "#FFFFFF" }}>{i + 1}</span>
                        {id} <X size={11} />
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <button data-testid="section-generate-btn" onClick={generateSection} disabled={sectionIds.length < 2 || sectionBusy}
                    className="flex-1 h-9 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40" style={{ background: "#C20511", color: "#FFFFFF" }}>
                    {sectionBusy ? <Loader2 size={14} className="animate-spin" /> : <Route size={14} />} Generate section
                  </button>
                  <button data-testid="section-clear-btn" onClick={clearSection} disabled={!sectionIds.length}
                    className="h-9 px-3 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
                    <Trash2 size={14} />
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 leading-snug">Pick 2+ holes to build a down-dip grade cross-section (A→A′). It is saved and added as a plate in the Master PDF.</p>
              </div>

              {selected ? (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="borehole-selected-card">
                  <div className="px-4 h-10 flex items-center justify-between gap-2" style={{ background: "#0B1420" }}>
                    <span className="text-sm font-bold truncate" style={{ color: "#FFFFFF" }}>{selected.hole_id}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {selected.validation && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded" data-testid="selected-validation"
                          style={{ background: VALIDATION_C[selected.validation], color: "#fff" }}>
                          {VALIDATION_LABEL[selected.validation]}
                        </span>
                      )}
                      {thmField && selected.grades?.[thmField] != null && (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded" style={{ background: thmColor(selected.grades[thmField]), color: "#0B1420" }}>
                          {thmField} {selected.grades[thmField]}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="text-[11px] text-slate-500 mb-1">
                      {selected.lat.toFixed(5)}, {selected.lon.toFixed(5)}{selected.depth_m != null ? ` · ${selected.depth_m} m depth` : ""}
                      {selected.intervals?.length ? ` · ${selected.intervals.length} intervals` : ""}
                    </div>
                    {selected.snap && (
                      <div className="text-[11px] font-semibold mb-2" style={{ color: "#92400E" }} data-testid="selected-snap">
                        Nearest corridor: {selected.snap.corridor_id} · {selected.snap.distance_m} m
                      </div>
                    )}
                    {Object.keys(selected.grades || {}).length ? (
                      <table className="w-full text-xs">
                        <tbody>
                          {(gradeFields.length ? gradeFields : Object.keys(selected.grades)).map((g) => (
                            selected.grades[g] != null ? (
                              <tr key={g} className="border-b border-slate-100 last:border-0">
                                <td className="py-1.5 text-slate-600">
                                  {g === thmField && <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle" style={{ background: thmColor(selected.grades[g]) }} />}
                                  {g}{selected.intervals?.length ? <span className="text-[9px] text-slate-400 ml-1">(comp)</span> : null}
                                </td>
                                <td className="py-1.5 text-right font-bold text-slate-900 tabular-nums">{selected.grades[g]}</td>
                              </tr>
                            ) : null
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-xs text-slate-400">No grades logged for this hole.</p>
                    )}

                    {/* Down-hole compositing - interval assay editor */}
                    <div className="mt-3 pt-3 border-t border-slate-100" data-testid="interval-editor">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Down-hole intervals</span>
                        {!editIntervals && (
                          <button data-testid="edit-intervals-btn" onClick={startEditIntervals}
                            className="text-[11px] font-semibold flex items-center gap-1 text-[#92400E] hover:underline">
                            <Plus size={12} /> {selected.intervals?.length ? "Edit" : "Add intervals"}
                          </button>
                        )}
                      </div>
                      {!editIntervals ? (
                        selected.intervals?.length ? (
                          <table className="w-full text-[11px]" data-testid="interval-view">
                            <thead><tr className="text-slate-400 text-left">
                              <th className="font-semibold py-0.5">From</th><th className="font-semibold">To</th>
                              <th className="font-semibold text-right">{thmField || "THM"}</th>
                            </tr></thead>
                            <tbody>
                              {selected.intervals.map((iv, i) => (
                                <tr key={i} className="border-t border-slate-100">
                                  <td className="py-0.5 tabular-nums">{iv.from_m}</td>
                                  <td className="tabular-nums">{iv.to_m}</td>
                                  <td className="text-right tabular-nums font-semibold">
                                    <span className="inline-block w-2 h-2 rounded-sm mr-1 align-middle" style={{ background: thmColor(thmField ? iv.grades?.[thmField] : null) }} />
                                    {thmField ? (iv.grades?.[thmField] ?? "-") : "-"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="text-[11px] text-slate-400">Collar grade only. Add intervals for a true grade-by-depth section.</p>
                        )
                      ) : (
                        <div className="space-y-1.5" data-testid="interval-draft">
                          <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1 text-[10px] text-slate-400 font-semibold px-0.5">
                            <span>From (m)</span><span>To (m)</span><span>{thmField || "THM"} %</span><span />
                          </div>
                          {draftIntervals.map((iv, i) => (
                            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1 items-center">
                              <input data-testid={`draft-from-${i}`} type="number" value={iv.from_m}
                                onChange={(e) => updateDraft(i, "from_m", e.target.value)}
                                className="h-7 px-1.5 rounded border border-slate-200 text-[11px] w-full" />
                              <input data-testid={`draft-to-${i}`} type="number" value={iv.to_m}
                                onChange={(e) => updateDraft(i, "to_m", e.target.value)}
                                className="h-7 px-1.5 rounded border border-slate-200 text-[11px] w-full" />
                              <input data-testid={`draft-thm-${i}`} type="number" value={thmField ? (iv.grades?.[thmField] ?? "") : ""}
                                onChange={(e) => updateDraftGrade(i, thmField, e.target.value)} disabled={!thmField}
                                className="h-7 px-1.5 rounded border border-slate-200 text-[11px] w-full disabled:bg-slate-50" />
                              <button data-testid={`draft-remove-${i}`} onClick={() => removeDraft(i)} className="text-slate-400 hover:text-red-500 px-1"><X size={13} /></button>
                            </div>
                          ))}
                          <button data-testid="draft-add-btn" onClick={addDraftInterval}
                            className="w-full h-7 rounded border border-dashed border-slate-300 text-[11px] font-semibold text-slate-500 hover:border-[#C20511] hover:text-[#92400E] flex items-center justify-center gap-1">
                            <Plus size={12} /> Add interval
                          </button>
                          <div className="flex gap-1.5 pt-1">
                            <button data-testid="intervals-save-btn" onClick={saveIntervals} disabled={intervalBusy}
                              className="flex-1 h-8 rounded-md text-[11px] font-semibold flex items-center justify-center gap-1 disabled:opacity-40" style={{ background: "#C20511", color: "#FFFFFF" }}>
                              {intervalBusy ? <Loader2 size={13} className="animate-spin" /> : null} Save &amp; composite
                            </button>
                            <button data-testid="intervals-cancel-btn" onClick={() => setEditIntervals(false)}
                              className="h-8 px-3 rounded-md text-[11px] font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50">Cancel</button>
                          </div>
                          {!thmField && <p className="text-[10px] text-red-600">No THM grade field detected for this dataset.</p>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-slate-400 leading-snug px-1" data-testid="borehole-help">
                  Markers are coloured by THM grade - click any hole for grades, corridor distance, verdict and down-hole intervals. Toggle Surface for the interpolated grade heatmap and Validate for the Model-vs-Ground-Truth view. Everything here is added to the Master PDF.
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    <Dialog open={bulkOpen} onOpenChange={(v) => { if (!bulkBusy) setBulkOpen(v); }}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden [&>button]:hidden" data-testid="bulk-import-dialog">
        <div className="flex items-center justify-between px-5 h-12 shrink-0" style={{ background: "#0B1420" }}>
          <DialogTitle className="text-sm font-bold flex items-center gap-2" style={{ color: "#FFFFFF", fontFamily: "Manrope, sans-serif" }}>
            <ClipboardList size={15} /> Guided Bulk Import - Multi-hole interval assays
          </DialogTitle>
          <button data-testid="bulk-close" onClick={() => !bulkBusy && setBulkOpen(false)}
            className="h-8 w-8 grid place-items-center rounded-md text-slate-300 hover:bg-white/10"><X size={16} /></button>
        </div>
        <DialogDescription className="sr-only">Paste or load a multi-hole interval CSV, preview the parsed drillholes, then import them all at once.</DialogDescription>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-auto bg-slate-50">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">1 · Paste or load CSV</span>
              <div className="flex items-center gap-2">
                <input ref={bulkFileRef} type="file" accept=".csv,text/csv" onChange={onBulkFile} className="hidden" data-testid="bulk-file-input" />
                <button data-testid="bulk-choose-file" onClick={() => bulkFileRef.current?.click()}
                  className="h-7 px-2.5 rounded-md text-[11px] font-semibold flex items-center gap-1 border border-slate-300 text-slate-600 hover:bg-white"><Upload size={12} /> Choose file</button>
                <button data-testid="bulk-load-sample" onClick={() => { setBulkText(buildSampleCsv()); setBulkPreview(null); }}
                  className="h-7 px-2.5 rounded-md text-[11px] font-semibold flex items-center gap-1 border border-slate-300 text-slate-600 hover:bg-white"><FileText size={12} /> Load sample</button>
              </div>
            </div>
            <textarea data-testid="bulk-textarea" value={bulkText}
              onChange={(e) => { setBulkText(e.target.value); setBulkPreview(null); }}
              placeholder={"hole_id,lon,lat,from_m,to_m,thm_pct,...\nDH001,39.2401,-6.8123,0,4,3.1,...\nDH001,39.2401,-6.8123,4,9,7.8,..."}
              className="w-full h-36 rounded-md border border-slate-300 p-2.5 text-[11px] font-mono resize-none focus:outline-none focus:ring-2 focus:ring-[#C20511]" />
            <div className="flex items-center justify-between gap-3">
              <p className="text-[10px] text-slate-500 leading-snug">One row per interval per hole; repeat the <b>hole_id</b> + collar lon/lat across its intervals. SYMIN groups &amp; composites each hole automatically.</p>
              <button data-testid="bulk-preview-btn" onClick={runBulkPreview} disabled={bulkBusy || !bulkText.trim()}
                className="h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40 shrink-0" style={{ background: "#0B1420", color: "#FFFFFF" }}>
                {bulkBusy && !bulkPreview ? <Loader2 size={13} className="animate-spin" /> : <Table size={13} />} Preview
              </button>
            </div>
          </div>

          {bulkPreview && (
            <div className="space-y-3" data-testid="bulk-preview">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">2 · Review &amp; import</span>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="rounded-lg border border-slate-200 bg-white p-2"><div className="text-lg font-bold text-slate-900 tabular-nums" data-testid="bulk-count">{bulkPreview.count}</div><div className="text-[10px] text-slate-500">Holes</div></div>
                <div className="rounded-lg border border-slate-200 bg-white p-2"><div className="text-lg font-bold text-slate-900 tabular-nums">{bulkPreview.interval_holes}</div><div className="text-[10px] text-slate-500">With intervals</div></div>
                <div className="rounded-lg border border-slate-200 bg-white p-2"><div className="text-lg font-bold text-slate-900 tabular-nums">{bulkPreview.grade_fields?.length || 0}</div><div className="text-[10px] text-slate-500">Grade fields</div></div>
                <div className="rounded-lg border border-slate-200 bg-white p-2"><div className="text-base font-bold tabular-nums truncate" style={{ color: "#92400E" }}>{bulkPreview.thm_field || "-"}</div><div className="text-[10px] text-slate-500">THM field</div></div>
              </div>
              {bulkPreview.issues?.length > 0 && (
                <div className="space-y-1.5" data-testid="bulk-issues">
                  {bulkPreview.issues.map((it, i) => (
                    <div key={i} data-testid={`bulk-issue-${it.code}`}
                      className="flex items-start gap-2 text-[11px] font-semibold rounded px-2 py-1.5 border"
                      style={it.level === "error"
                        ? { background: "#FEF2F2", borderColor: "#FECACA", color: "#B91C1C" }
                        : { background: "#FFFBEB", borderColor: "#F6CBCB", color: "#92400E" }}>
                      {it.level === "error" ? <AlertOctagon size={13} className="mt-[1px] shrink-0" /> : <AlertTriangle size={13} className="mt-[1px] shrink-0" />}
                      <span>{it.message}</span>
                    </div>
                  ))}
                </div>
              )}
              {bulkPreview.grade_fields?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {bulkPreview.grade_fields.map((g) => (<span key={g} className="text-[10px] font-semibold px-2 py-0.5 rounded" style={{ background: "#FDECEC", color: "#92400E" }}>{g}</span>))}
                </div>
              )}
              {bulkOut > 0 && (
                <div className="text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5" data-testid="bulk-outside-note">
                  {bulkOut} hole{bulkOut > 1 ? "s" : ""} fall outside this licence AOI - check the coordinate system before importing.
                </div>
              )}
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="max-h-52 overflow-auto">
                  <table className="w-full text-[11px]" data-testid="bulk-preview-table">
                    <thead className="sticky top-0" style={{ background: "#0B1420" }}>
                      <tr className="text-left" style={{ color: "#FFFFFF" }}>
                        <th className="font-semibold px-2 py-1.5">Hole</th><th className="font-semibold px-2">Lon</th><th className="font-semibold px-2">Lat</th>
                        <th className="font-semibold px-2 text-right">Depth</th><th className="font-semibold px-2 text-right">Intervals</th>
                        <th className="font-semibold px-2 text-right">{bulkPreview.thm_field || "THM"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkPreview.boreholes.slice(0, 50).map((h) => {
                        const flags = bulkPreview.row_flags?.[h.hole_id] || [];
                        const flagged = flags.length > 0;
                        const flagLabel = { overlapping_intervals: "Overlapping depth intervals", dropped_intervals: "Some interval rows ignored (bad From/To)", no_grade: "No numeric grade values" };
                        const title = flags.map((ff) => flagLabel[ff] || ff).join(" · ");
                        return (
                        <tr key={h.hole_id} className={`border-t border-slate-100 ${flagged ? "bg-red-50" : "bg-white"}`} data-testid={`bulk-row-${h.hole_id}`} title={flagged ? title : undefined}>
                          <td className="px-2 py-1 font-semibold text-slate-800">
                            {flagged && <AlertTriangle size={11} className="inline mr-1 align-middle" style={{ color: "#B45309" }} data-testid={`bulk-rowflag-${h.hole_id}`} />}
                            {h.hole_id}
                          </td>
                          <td className="px-2 tabular-nums text-slate-600">{h.lon}</td>
                          <td className="px-2 tabular-nums text-slate-600">{h.lat}</td>
                          <td className="px-2 text-right tabular-nums text-slate-600">{h.depth_m ?? "-"}</td>
                          <td className="px-2 text-right tabular-nums text-slate-600">{h.intervals?.length ?? 0}</td>
                          <td className="px-2 text-right tabular-nums font-semibold">
                            <span className="inline-block w-2 h-2 rounded-sm mr-1 align-middle" style={{ background: thmColor(bulkPreview.thm_field ? h.grades?.[bulkPreview.thm_field] : null) }} />
                            {bulkPreview.thm_field ? (h.grades?.[bulkPreview.thm_field] ?? "-") : "-"}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {bulkPreview.count > 50 && <div className="px-2 py-1 text-[10px] text-slate-400 bg-slate-50 border-t border-slate-100">Showing first 50 of {bulkPreview.count} holes.</div>}
              </div>
              <p className="text-[10px] text-slate-400">Importing replaces the drillholes currently loaded for this area.</p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 h-14 border-t border-slate-200 bg-white shrink-0">
          <button data-testid="bulk-cancel-btn" onClick={() => !bulkBusy && setBulkOpen(false)}
            className="h-9 px-4 rounded-md text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50">Cancel</button>
          <button data-testid="bulk-import-btn" onClick={commitBulk} disabled={bulkBusy || !bulkPreview?.count}
            className="h-9 px-4 rounded-md text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40" style={{ background: "#C20511", color: "#FFFFFF" }}>
            {bulkBusy && bulkPreview ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Import {bulkPreview?.count ? `${bulkPreview.count} hole${bulkPreview.count > 1 ? "s" : ""}` : "holes"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
